import React from "react";
import { useTranslation } from "react-i18next";
import { useComparisonLabels } from "../../../compare/ComparisonLabelsContext";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import styles from "./simulazioneAndamentoPortafoglio.module.scss";
import { formatLocalizedDate } from "utils/dateUtils";
import { formatItalianNumber } from "utils/formatUtils";

type ComponentProps = { data: any; data2?: any };
const SimulazioneGraficoVolatilita: React.FC<ComponentProps> = ({
  data,
  data2,
}) => {
  const { t, i18n } = useTranslation();
  const labels = useComparisonLabels();
  if (!data) {
    return null;
  }
  const dataVolatilita =
    data.analisi_backtest.simulazione_grafico_volatilita_1_anno_portafoglio;
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const filteredData = dataVolatilita.map((item) => {
    const dateObj = new Date(item.date);
    const year = dateObj.getFullYear();

    return {
      ...item,
      originalDate: item.date,
      date: item.date,
      year,
      value: parseFloat(item.value),
    };
  });

  let combinedData = [...filteredData];
  if (
    data2 &&
    data2.analisi_backtest &&
    data2.analisi_backtest.simulazione_grafico_volatilita_1_anno_portafoglio
  ) {
    const data2Andamento =
      data2.analisi_backtest.simulazione_grafico_volatilita_1_anno_portafoglio;

    const dateMap = new Map();
    filteredData.forEach((item) => {
      dateMap.set(item.originalDate, item);
    });

    data2Andamento.forEach((item) => {
      const dateObj = new Date(item.date);
      const formattedDate = dateObj.getFullYear();
      const value2 = parseFloat(item.value);

      const existingEntry = dateMap.get(item.date);
      if (existingEntry) {
        const index = combinedData.findIndex(
          (d) => d.originalDate === item.date,
        );
        if (index !== -1) {
          combinedData[index] = {
            ...combinedData[index],
            value2: value2,
          };
        }
      } else {
        combinedData.push({
          ...item,
          originalDate: item.date,
          date: formattedDate,
          value2: value2,
        });
      }
    });

    // Sort combined data by date
    combinedData.sort(
      (a, b) =>
        new Date(a.originalDate).getTime() - new Date(b.originalDate).getTime(),
    );
  }

  const chartCopy = {
    fontSize: 11,
    fill: colorsConfig.finecoBlack,
    fontFamily: "Arial",
  };

  const allReturns = combinedData
    .flatMap((d) => [
      d.value !== undefined ? d.value : [],
      d.value2 !== undefined ? d.value2 : [],
    ])
    .filter((val) => val !== undefined && val !== null);

  // Calculate the actual data range
  const actualMin = Math.min(...allReturns);
  const actualMax = Math.max(...allReturns);
  const dataRange = actualMax - actualMin;

  // Set appropriate buffer based on data range
  // If range is small (less than 50% of the minimum value), add a smaller buffer
  const isSmallRange = dataRange < actualMin * 0.5;
  const bufferPercentage = isSmallRange ? 0.1 : 0.05; // 10% buffer for small ranges, 5% for larger ranges

  // Calculate min and max with buffer - don't force zero as minimum
  const minReturn = Math.floor(actualMin * (1 - bufferPercentage));
  const maxReturn = Math.ceil(actualMax * (1 + bufferPercentage));

  const returnDiff = maxReturn - minReturn;
  let tickStep = returnDiff < 60 ? 10 : 50;
  tickStep = returnDiff < 40 ? 5 : tickStep;
  tickStep = returnDiff < 20 ? 2 : tickStep;
  tickStep = returnDiff < 10 ? 1 : tickStep;

  // Custom tooltip with background
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Format the original date
      const originalDate = new Date(payload[0].payload.originalDate);
      const formattedDate = formatLocalizedDate(originalDate, i18n.language);

      return (
        <div
          className={styles.tooltipContainer}
          style={{
            backgroundColor: "white",
            border: `1px solid ${colorsConfig.FinecoGrayLight1}`,
            padding: "8px 10px",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <p className={styles.tooltipLabel} style={{ margin: "0 0 5px 0" }}>
            {formattedDate}
          </p>
          {payload.map((entry: any, index: number) => {
            // Skip undefined values
            if (entry.value === undefined) return null;

            // Determine the portfolio name based on dataKey
            const portfolioName =
              entry.dataKey === "value" ? labels.portfolio1 : labels.portfolio2;

            return (
              <p
                key={`value-${index}`}
                style={{
                  color: entry.color,
                  margin: "3px 0",
                  fontSize: "12px",
                }}
              >
                {data2 ? portfolioName : t(`portfolio.${entry.name}`)}:{" "}
                {formatItalianNumber(entry.value)}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const CustomizedLegend = (props: any) => {
    if (!data2) return null;
    const legendItems = [
      {
        value: labels.portfolio1,
        color: colorsConfig.portfolio1,
      },
      {
        value: labels.portfolio2,
        color: colorsConfig.portfolio2,
      },
    ];

    return (
      <div className={styles.legendContainer}>
        {legendItems.map((entry, index) => (
          <div key={`item-${index}`} className={styles.legendItem}>
            <div
              className={styles.legendDot}
              style={{ backgroundColor: entry.color }}
            />
            <span className={styles.legendCopy}>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const uniqueYears = Array.from(
    new Set(combinedData.map((d) => new Date(d.originalDate).getFullYear())),
  );

  return (
    <div className={styles.container}>
      <h2 className={styles.sectionTitle}>
        {t("portfolio.simulazioneGraficoVolatilita")}
      </h2>
      <div className={styles.chartContainer}>
        <ResponsiveContainer debounce={500}>
          <LineChart
            data={combinedData}
            margin={{ top: 10, right: 10, left: -25, bottom: 0 }}
          >
            <CartesianGrid
              strokeWidth={1}
              stroke={colorsConfig.GrayLight2}
              strokeOpacity={1}
              horizontal={true}
              vertical={false}
            />
            <XAxis
              dataKey="year"
              ticks={uniqueYears}
              tick={{ ...chartCopy }}
              tickLine={false}
              axisLine={{ stroke: colorsConfig.GrayLight2 }}
            />
            <ReferenceLine
              y={0}
              stroke={colorsConfig.FinecoBlack}
              strokeWidth={1}
            />
            <YAxis
              tick={chartCopy}
              tickCount={Math.ceil(returnDiff / tickStep)}
              domain={[0, maxReturn]}
              tickLine={false}
              tickFormatter={(value) => Math.round(value).toString()}
              ticks={Array.from(
                { length: Math.ceil(returnDiff / tickStep) + 1 },
                (_, i) => Math.floor(0 / tickStep) * tickStep + i * tickStep,
              )}
              axisLine={{ stroke: colorsConfig.GrayLight2 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomizedLegend />} />
            <Line
              name={data2 ? labels.portfolio1 : "value"}
              type="natural"
              dataKey="value"
              stroke={colorsConfig.FinecoBluePrimary}
              dot={false}
              strokeWidth={1.5}
              strokeOpacity={1}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
            {data2 && (
              <Line
                name={labels.portfolio2}
                type="natural"
                dataKey="value2"
                stroke={colorsConfig.CardCardGreen}
                dot={false}
                strokeWidth={1.5}
                strokeOpacity={1}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SimulazioneGraficoVolatilita;
